/**
 * Find case references in free-form Swedish legal prose and normalise them to
 * the canonical ids used by the corpus / external_cases table
 * (`nja:2014:s:684`, `hfd:2016:ref:37`, `ad:2015:12`, `mod:2015:12`, …).
 *
 * Mirror of the case patterns in the pipeline's `parsing/refs.py` and the
 * API's `ask.canonicalise_ref` — keep the three in sync when adding a court.
 * Only the five shapes present in external_cases are linkable, but a match
 * that is not in the index is simply left as plain text.
 */

export interface CaseRefMatch {
  /** Match start offset in the source string. */
  start: number;
  /** Match end offset (exclusive). */
  end: number;
  /** Canonical id, e.g. "nja:2014:s:684". */
  refId: string;
}

interface PatternDef {
  re: RegExp;
  build: (m: RegExpExecArray) => string;
}

const PATTERNS: PatternDef[] = [
  // NJA YYYY s. N / NJA YYYY not. N
  { re: /\bNJA\s+(\d{4})\s+s\.?\s*(\d{1,4})\b/gi, build: (m) => `nja:${m[1]}:s:${+m[2]}` },
  { re: /\bNJA\s+(\d{4})\s*not\.?\s*(\d{1,3})\b/gi, build: (m) => `nja:${m[1]}:not:${+m[2]}` },
  // HFD YYYY ref. N / not. N / colon shorthand YYYY:N (stored as :ref:)
  { re: /\bHFD\s+(\d{4})\s*ref\.?\s*(\d{1,3})\b/gi, build: (m) => `hfd:${m[1]}:ref:${+m[2]}` },
  { re: /\bHFD\s+(\d{4})\s*not\.?\s*(\d{1,3})\b/gi, build: (m) => `hfd:${m[1]}:not:${+m[2]}` },
  { re: /\bHFD\s+(\d{4})\s*:\s*(\d{1,3})\b/gi, build: (m) => `hfd:${m[1]}:ref:${+m[2]}` },
  // RÅ (accepts RÅ/Ra) YYYY ref. N / not. N / YYYY:N
  { re: /\bR[ÅA]\s+(\d{4})\s*ref\.?\s*(\d{1,3})\b/gi, build: (m) => `ra:${m[1]}:ref:${+m[2]}` },
  { re: /\bR[ÅA]\s+(\d{4})\s*not\.?\s*(\d{1,3})\b/gi, build: (m) => `ra:${m[1]}:not:${+m[2]}` },
  { re: /\bR[ÅA]\s+(\d{4})\s*:\s*(\d{1,3})\b/gi, build: (m) => `ra:${m[1]}:ref:${+m[2]}` },
  // AD YYYY nr. N / YYYY:N
  { re: /\bAD\s+(\d{4})\s*nr\.?\s*(\d{1,3})\b/gi, build: (m) => `ad:${m[1]}:${+m[2]}` },
  { re: /\bAD\s+(\d{4})\s*:\s*(\d{1,3})\b/gi, build: (m) => `ad:${m[1]}:${+m[2]}` },
  // MÖD / MOD / MD YYYY:N (case-sensitive: "mod" is a Swedish word)
  { re: /\bM[ÖO]?D\s+(\d{4})\s*[:\s]\s*(\d{1,3})\b/g, build: (m) => `mod:${m[1]}:${+m[2]}` },
  // FÖD (no rows in external_cases today, but keep parity with refs.py)
  { re: /\bF[ÖO]D\s+(\d{4})\s*[:\s]\s*(\d{1,3})\b/g, build: (m) => `fod:${m[1]}:${+m[2]}` },
];

/** Case references found in *text*, in source order, without overlaps. */
export function findCaseRefs(text: string): CaseRefMatch[] {
  if (!text) return [];
  const hits: CaseRefMatch[] = [];
  for (const { re, build } of PATTERNS) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      hits.push({ start: m.index, end: m.index + m[0].length, refId: build(m) });
      if (m.index === re.lastIndex) re.lastIndex++; // zero-length guard
    }
  }
  // Earliest first; at the same start keep the longest match.
  hits.sort((a, b) => a.start - b.start || b.end - a.end);
  const out: CaseRefMatch[] = [];
  let lastEnd = -1;
  for (const h of hits) {
    if (h.start < lastEnd) continue;
    out.push(h);
    lastEnd = h.end;
  }
  return out;
}
